/**
 * @module Store
 *
 * @desc
 *
 * The actual store rendeering starts from here. This component is responsible to fetch all the data
 * required to present the store to the user.
 *
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HashRouter as Router } from 'react-router-dom';
import { initialize } from '../redux_stores/localizeReducer/actions';

import {
	getAllScenes,
	getComponentConfig,
	getDefaultIcons,
	getStoreAvatars,
	getStoreData,
} from '../apis/webStoreAPI';
import UILayer from './loaders/UILayer';
import ModulesLayer from './loaders/ModulesLayer';
import AnalyticsLayer from './loaders/AnalyticsLayer';
import { getLocaleFromHtml } from '../utils/htmlHelpers';
import { setWebpSupport } from '../redux_stores/storeDataReducer/actions';
import { setupCreatorTools } from '../redux_stores/creatorTools/actions';
import ContentScripts from './loaders/ContentScripts';
import { createSelector } from '@reduxjs/toolkit';

/**
 * @description
 * - Selects store data from redux store.
 * - Add additional properties to select from redux store here.
 * @returns {
 * 	{
 * 		defaultLocale: *,
 * 		locales: *,
 * 		storeDataLoaded: boolean,
 * 		compConfigLoaded: *,
 * 		storeId: string,
 * 		localesEnabled: boolean
 * 	}
 * }
 *
 */
const selectStoreData = createSelector(
	[
		(state) => state?.storeData?.id,
		(state) => state?.storeData?.loaded || false,
		(state) => state?.storeData?.localization?.locales || false,
		(state) => state?.storeData?.localization?.enabled || false,
		(state) => state?.storeData?.localization?.default_locale || false,
		(state) => state?.componentConfig?.loaded || false,
	],
	(
		storeId,
		storeDataLoaded,
		locales,
		enabled,
		default_locale,
		compConfigLoaded,
	) => {
		return {
			storeId: storeId,
			storeDataLoaded: storeDataLoaded,
			compConfigLoaded,
			locales: locales,
			localesEnabled: enabled,
			defaultLocale: default_locale,
		};
	},
);

/**
 * Store component
 * @description
 * Renders the entire store page. This is entry point of the store.
 * This component is wrapped in a redux provider which can be accessed by all the children components.
 * @returns {*|JSX.Element}
 *
 */

const Store = () => {
	console.log('%c Store rerendered', 'color:red');
	const dispatch = useDispatch();

	const {
		storeId,
		storeDataLoaded,
		compConfigLoaded,
		locales,
		localesEnabled,
		defaultLocale,
	} = useSelector(selectStoreData);

	/**
	 * Fetch all the initial store data here
	 */
	useEffect(() => {
		dispatch(getComponentConfig(storeId));
		dispatch(getStoreAvatars(storeId));
		dispatch(getStoreData(storeId));
		dispatch(getDefaultIcons());
		dispatch(getAllScenes(storeId));
		dispatch(setWebpSupport());
		dispatch(setupCreatorTools());
	}, []);

	/**
	 * @function getLocaleFromHashPath
	 *
	 * Get locale from hashPath
	 *
	 * @returns {string|null}
	 *
	 * @description
	 *
	 * 1. Check if hashPath exists
	 * 2. Split hashPath by '/'
	 * 3. If hashPathParts.length >= 2 and hashPathParts[1] is a locale, return hashPathParts[1]. e.g. #/en => en
	 *
	 * @example
	 * - #/ => null
	 * - #/en => en
	 * - #/en/sceneName => en
	 */
	const getLocaleFromHashPath = () => {
		const hashpath = window.location.hash;

		if (!hashpath) {
			return null;
		}

		const hashPathParts = hashpath.split('/');
		if (hashPathParts.length >= 2) {
			if (locales.includes(hashPathParts[1])) {
				return hashPathParts[1];
			}
		}

		return null;
	};

	/**
	 * Get active locale
	 *
	 * @description
	 * 1. Check if locales are enabled
	 * 2. If not, return null
	 * 3. If enabled follow the following priorities
	 *   1. Get locale from hashPath `getLocaleFromHashPath`
	 *   2. Get locale from html input var named `locale`. This is used in embeds to set default locales for the page.
	 *   3. Return default locale
	 * 5. Return null
	 *
	 * @returns {*|string|null}
	 */
	const getActiveLocale = () => {
		/*
		 * Priorities
		 * -
		 * */
		if (!localesEnabled) {
			return null;
		}

		/* Return locale from hashPath */
		const localeFromHashPath = getLocaleFromHashPath();
		if (localeFromHashPath && localeFromHashPath !== '') {
			return localeFromHashPath;
		}

		/* Return locale from html input var */
		const localeFromHtml = getLocaleFromHtml();
		if (localeFromHtml && localeFromHtml !== '') {
			return localeFromHtml;
		}

		if (defaultLocale) {
			return defaultLocale;
		}

		return null;
	};

	/**
	 * Initialize localize store if localization is enabled on the store.
	 */
	useEffect(() => {
		if (storeDataLoaded && localesEnabled) {
			dispatch(
				initialize({
					locales,
					defaultLocale,
					activeLocale: getActiveLocale(),
					fallBackLocale: defaultLocale,
				}),
			);
		}
	}, [storeDataLoaded]);

	return (
		compConfigLoaded &&
		storeDataLoaded && (
			<Router>
				<UILayer />
				<ModulesLayer />
				<AnalyticsLayer locale={getActiveLocale()} />
				<ContentScripts storeId={storeId} />
			</Router>
		)
	);
};

export default Store;
